import {useEffect, useMemo, useCallback, useState} from 'react';
import {getFileVerification} from '@/services/fileUploadService';
import {Constants} from '@/utils/constants/commonConstants';
import {buildWebpImageUploadFormData} from '@/helpers/imageUploadForm';
import {
  ALERT_MIN,
  ALERT_MAX,
  IMAGE_SLOTS
} from '@/utils/constants/rewardsDisplayConstants';
import {
  createEmptyImages,
  clampAlertDisplayCount,
  getSanitizedAlertCount,
  shiftImagesAfterDelete,
  updateImageAtPosition
} from '@/utils/rewardsDisplay';

/**
 * Hook to manage image upload and display state
 */
export const useRewardsDisplayImages = ({
  display,
  activeProductId,
  updateProductDisplay,
  showToastNotification,
  fetchAndSetDefaultImages,
  currentStep,
  formData,
  handleFormChange
}) => {
  const [uploadInFlightCount, setUploadInFlightCount] = useState(0);
  const isImageUploading = uploadInFlightCount > 0;

  const beginImageUpload = useCallback(() => {
    setUploadInFlightCount((c) => c + 1);
  }, []);

  const endImageUpload = useCallback(() => {
    setUploadInFlightCount((c) => Math.max(0, c - 1));
  }, []);

  const useDefaultImageToggleValue =
    !!display?.productDisplay?.[activeProductId]?.useDefaultImage;

  // Images for active product (sorted)
  const imagesForActiveProduct = useMemo(() => {
    const current = display?.productDisplay?.[activeProductId] || {};
    return (current?.images || createEmptyImages())
      .slice()
      .sort((a, b) => Number(a?.position) - Number(b?.position));
  }, [display?.productDisplay, activeProductId]);

  // Alert display count
  const alertDisplayCount = getSanitizedAlertCount(
    display?.productDisplay?.[activeProductId]?.alertDisplayCount
  );

  const setAlertDisplayCount = useCallback(
    (val) => {
      if (val === '' || val === null || val === undefined) {
        updateProductDisplay({alertDisplayCount: ''});
        return;
      }
      updateProductDisplay({
        alertDisplayCount: clampAlertDisplayCount(val, ALERT_MIN, ALERT_MAX)
      });
    },
    [updateProductDisplay]
  );

  // Show on homepage - now always at reward level (formData.showOnHomePage) for all reward types
  // Toggle moved to Attribute tab (AdditionalAttributesSection), but hook still exposes for backward compatibility
  const showOnHomePage = !!formData?.showOnHomePage;

  const handleShowOnHomePageChange = useCallback(
    (value) => {
      handleFormChange('showOnHomePage', Boolean(value));
    },
    [handleFormChange]
  );

  // Toggle default image handler
  const handleUseDefaultImageToggleChange = useCallback(
    (value) => {
      const current = display?.productDisplay?.[activeProductId] || {};

      if (value) {
        const productData =
          formData?.fetchedProducts?.find(
            (p) => p.productId === activeProductId
          ) || {};
        const hasBackup = !!current.customImagesBackup;
        const backup = hasBackup
          ? undefined
          : Array.isArray(current.images) && productData?.imageUrls?.length > 0
            ? current.images.map((i) => ({...i}))
            : createEmptyImages();
        const update = backup
          ? {useDefaultImage: true, customImagesBackup: backup, images: backup}
          : {useDefaultImage: true};
        updateProductDisplay(update);

        if (activeProductId) {
          Promise.resolve(fetchAndSetDefaultImages(activeProductId))
            .then((ok) => {
              if (ok !== false) return;

              const nextImages = Array.isArray(backup)
                ? backup
                : Array.isArray(current.customImagesBackup)
                  ? current.customImagesBackup
                  : Array.isArray(current.images)
                    ? current.images
                    : createEmptyImages();

              updateProductDisplay({
                useDefaultImage: false,
                customImagesBackup: undefined,
                images: nextImages
              });
            })
            .catch(() => {
              const nextImages = Array.isArray(backup)
                ? backup
                : Array.isArray(current.customImagesBackup)
                  ? current.customImagesBackup
                  : Array.isArray(current.images)
                    ? current.images
                    : createEmptyImages();

              updateProductDisplay({
                useDefaultImage: false,
                customImagesBackup: undefined,
                images: nextImages
              });
            });
        }
      } else {
        updateProductDisplay({
          useDefaultImage: false,
          images: createEmptyImages(),
          customImagesBackup: undefined
        });
      }
    },
    [
      display?.productDisplay,
      activeProductId,
      updateProductDisplay,
      fetchAndSetDefaultImages,
      formData?.fetchedProducts
    ]
  );

  // Auto-fetch defaults when toggle is on and images are empty
  useEffect(() => {
    if (currentStep !== 2 || !activeProductId || !useDefaultImageToggleValue)
      return;

    const current = display?.productDisplay?.[activeProductId] || {};
    const imgs = current.images || [];
    const hasAny = Array.isArray(imgs) && imgs.some((i) => i?.url);

    if (!hasAny) {
      Promise.resolve(fetchAndSetDefaultImages(activeProductId))
        .then((ok) => {
          if (ok !== false) return;
          updateProductDisplay({
            useDefaultImage: false,
            images: createEmptyImages(),
            customImagesBackup: undefined
          });
        })
        .catch(() => {
          updateProductDisplay({
            useDefaultImage: false,
            images: createEmptyImages(),
            customImagesBackup: undefined
          });
        });
    }
  }, [
    currentStep,
    activeProductId,
    useDefaultImageToggleValue,
    display?.productDisplay,
    fetchAndSetDefaultImages,
    updateProductDisplay
  ]);

  const extractCdnUrl = useCallback((response) => {
    if (!response) return undefined;

    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : [];

    const first = list[0];
    return first?.media?.cdnUrl || first?.cdnUrl || first?.url;
  }, []);

  // Shared image upload service
  const uploadImageToService = useCallback(
    async (file) => {
      const {formData} = await buildWebpImageUploadFormData(file);

      const response = await getFileVerification(
        formData,
        Constants.Media,
        undefined,
        'webp'
      );
      return extractCdnUrl(response);
    },
    [extractCdnUrl]
  );

  // Image select/delete handler for a given position
  const onImageSelectAt = useCallback(
    (position) => async (file) => {
      if (useDefaultImageToggleValue) return;

      const images = imagesForActiveProduct.slice();

      // Delete: compact left and clear last slot
      if (!file) {
        const idx = Math.max(0, Math.min(IMAGE_SLOTS - 1, position - 1));
        updateProductDisplay({images: shiftImagesAfterDelete(images, idx)});
        return;
      }

      // Optimistic preview
      let previewUrl;
      beginImageUpload();
      try {
        previewUrl = URL.createObjectURL(file);
        updateProductDisplay({
          images: updateImageAtPosition(images, position, {
            url: previewUrl,
            file
          })
        });

        const cdnUrl = await uploadImageToService(file);
        if (cdnUrl) {
          const currentImages =
            display?.productDisplay?.[activeProductId]?.images || images;
          updateProductDisplay({
            images: updateImageAtPosition(currentImages, position, {
              url: cdnUrl,
              file: null
            })
          });
        } else {
          updateProductDisplay({
            images: updateImageAtPosition(images, position, {
              url: '',
              file: null
            })
          });
          showToastNotification('Image upload failed. Please try again.');
        }
      } catch {
        showToastNotification('Image upload failed. Please try again.');
        updateProductDisplay({
          images: updateImageAtPosition(images, position, {url: '', file: null})
        });
      } finally {
        endImageUpload();
        // Do not revoke preview blob URLs here.
        // Revoking can be racy with React rerenders/product switches and can lead to intermittent broken previews.
        // The final persisted URL should be the CDN URL; the preview blob's memory impact is bounded (max 3 images).
        void previewUrl;
      }
    },
    [
      useDefaultImageToggleValue,
      imagesForActiveProduct,
      updateProductDisplay,
      uploadImageToService,
      beginImageUpload,
      endImageUpload,
      showToastNotification,
      display?.productDisplay,
      activeProductId
    ]
  );

  // Single image upload for non-product reward types
  const imageUpload = useCallback(
    () => async (file) => {
      let previewUrl;
      try {
        if (!file) {
          handleFormChange('imageUrls', []);
          return;
        }

        beginImageUpload();
        previewUrl = URL.createObjectURL(file);
        handleFormChange('imageUrls', [previewUrl]);

        const cdnUrl = await uploadImageToService(file);
        if (cdnUrl) {
          handleFormChange('imageUrls', [cdnUrl]);
        } else {
          handleFormChange('imageUrls', []);
          showToastNotification('Image upload failed. Please try again.');
        }
      } catch {
        handleFormChange('imageUrls', []);
      } finally {
        if (file) endImageUpload();
        // Do not revoke preview blob URLs here.
        void previewUrl;
      }
    },
    [
      handleFormChange,
      uploadImageToService,
      beginImageUpload,
      endImageUpload,
      showToastNotification
    ]
  );

  return {
    useDefaultImageToggleValue,
    handleUseDefaultImageToggleChange,
    isImageUploading,
    alertDisplayCount,
    setAlertDisplayCount,
    showOnHomePage,
    handleShowOnHomePageChange,
    imagesForActiveProduct,
    onImageSelectAt,
    imageUpload
  };
};
